import { AxiosResponse } from 'axios';
import {
  CampaignPlanConfig,
  CampaignPlanConfigResponse,
  CampaignPlanDetails,
  CampaignPlanErrorEvent,
  CampaignPlanErrorTitle,
  CampaignPlanFilterRequest,
  CampaignPlanResponse,
} from 'components/common/types/CampaignPlan.types';
import { CancelFunctions } from 'components/common/types';
import { CAMPAIGN_PLAN_EXPIRED_MESSAGE } from 'constants/campaignPlan';
import { HTTP_METHODS, URLS } from './constants';
import createRequest from './request';
import handleRequest from './common';

const throwCampaignPlanRequestError = (
  error: Error & { response?: { status?: number; data?: { message?: string; detail?: string; title?: string } } },
): never => {
  if (error.response?.status === 401)
    throw new CampaignPlanErrorEvent({
      message: CAMPAIGN_PLAN_EXPIRED_MESSAGE,
      title: CampaignPlanErrorTitle.UNAUTHORIZED,
    });

  if (error.response?.data) throw new CampaignPlanErrorEvent(error.response.data);

  throw error;
};

export const validateCampaignPlanResponse = (response: AxiosResponse<CampaignPlanResponse>): void => {
  const isCampaignPlanIsCancelled = (): boolean => response.status === 204;

  const isCampaignPlanSuccessResponse = (data: CampaignPlanResponse): data is CampaignPlanDetails => {
    return 'campaignId' in data && 'endDate' in data;
  };

  if (isCampaignPlanIsCancelled())
    throw new CampaignPlanErrorEvent({
      message: CAMPAIGN_PLAN_EXPIRED_MESSAGE,
      title: CampaignPlanErrorTitle.UNAUTHORIZED,
    });

  if (!isCampaignPlanSuccessResponse(response.data)) throw new CampaignPlanErrorEvent(response.data);
};

export const getCampaignPlan = async (
  token: string,
  cancelFunctions: CancelFunctions,
): Promise<CampaignPlanDetails> => {
  const { send, cancel } = createRequest({
    url: URLS().FETCH_CAMPAIGN_PLAN(token),
    method: HTTP_METHODS.GET,
    ignoreHeaders: true,
  });

  if (cancelFunctions.FETCH_CAMPAIGN_PLAN) cancelFunctions.FETCH_CAMPAIGN_PLAN();
  cancelFunctions.FETCH_CAMPAIGN_PLAN = cancel;

  try {
    const result: AxiosResponse<CampaignPlanResponse> = await send();

    validateCampaignPlanResponse(result);

    return result.data as CampaignPlanDetails;
  } catch (error) {
    return throwCampaignPlanRequestError(error);
  }
};

export const saveCampaignPlanConfig = async (
  campaignId: string,
  publisherId: string,
  config: CampaignPlanConfig,
  cancelFunctions: CancelFunctions,
): Promise<void> => {
  const reqOptions = {
    url: URLS().SAVE_CAMPAIGN_PLAN_CONFIG(publisherId, campaignId),
    method: HTTP_METHODS.POST,
    data: config,
  };

  return handleRequest<void>(reqOptions, cancelFunctions, 'SAVE_CAMPAIGN_PLAN_CONFIG');
};

export const getCampaignPlanConfig = async (
  campaignId: string,
  publisherId: string,
  cancelFunctions: CancelFunctions,
): Promise<CampaignPlanConfigResponse | undefined> => {
  const reqOptions = {
    url: URLS().FETCH_CAMPAIGN_PLAN_CONFIG(publisherId, campaignId),
    method: HTTP_METHODS.GET,
    ignoreNotifyError: true,
  };

  return handleRequest<CampaignPlanConfigResponse | undefined>(
    reqOptions,
    cancelFunctions,
    'FETCH_CAMPAIGN_PLAN_CONFIG',
  );
};

export const filterCampaignPlan = async (
  token: string,
  cancelFunctions: CancelFunctions,
  filters: CampaignPlanFilterRequest,
): Promise<CampaignPlanDetails> => {
  const { send, cancel } = createRequest({
    url: URLS().FETCH_CAMPAIGN_PLAN(token),
    method: HTTP_METHODS.POST,
    data: filters,
    ignoreHeaders: true,
  });

  if (cancelFunctions.FILTER_CAMPAIGN_PLAN) cancelFunctions.FILTER_CAMPAIGN_PLAN();
  cancelFunctions.FILTER_CAMPAIGN_PLAN = cancel;

  try {
    const result: AxiosResponse<CampaignPlanResponse> = await send();

    validateCampaignPlanResponse(result);

    return result.data as CampaignPlanDetails;
  } catch (error) {
    return throwCampaignPlanRequestError(error);
  }
};
